import { useCallback, useEffect, useState } from 'react';
import Sheet from '../app/components/Sheet';
import { useApp } from '../app/lib/AppContext';
import UsdaSheet from './UsdaSheet';

// Admin panel › Cần xem lại: ingredient names the AI used that matched no food in the table confidently (none, only
// loosely, or several), most frequent first. "Gán" opens the food's details first, then links the name to it (it becomes an everyday name for it);
// "Tìm trong USDA…" adds a food the table lacks and links the name to it; "Dùng số AI ước tính…" keeps the AI's latest
// estimate as a food of its own (fixed numbers from then on); "Bỏ qua" lets the AI estimate it each time. Only later analyses change; logged meals keep their numbers.
const TABS = [
  { id: 'open', label: 'Đang chờ' },
  { id: 'linked', label: 'Đã gán' },
  { id: 'not_in_table', label: 'Bỏ qua' },
];
const REASONS = { unmatched: 'Không tìm thấy', loose: 'Gần giống', ambiguous: 'Nhiều thực phẩm trùng tên' };
const FEATURES = { meal: 'Bữa ăn', menu: 'Thực đơn', swap: 'Đổi món', takeout: 'Đặt ngoài' };
const BUTTON = 'btn h-10 min-h-10 rounded-[12px] border-line bg-base-100 px-3 text-sm';
const num = (v, digits = 1) => (v == null ? '–' : Number(v).toLocaleString('vi-VN', { maximumFractionDigits: digits }));
const SOURCES = { vn: 'Bảng thành phần thực phẩm Việt Nam (2007)', usda: 'USDA FoodData Central', ai: 'AI ước tính (quản trị giữ lại)' };
const when = (iso) => new Date(iso).toLocaleDateString('vi-VN', { day: 'numeric', month: 'numeric' });

function FoodPicker({ onPick }) {
  const { api } = useApp();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const search = (e) => {
    e.preventDefault();
    if (q.trim()) api.get(`/admin/foods?q=${encodeURIComponent(q.trim())}`).then((d) => setResults(d.foods.slice(0, 8)));
  };
  return (
    <div className="flex flex-col gap-2">
      <form className="flex gap-2" onSubmit={search}>
        <input className="input h-10 w-full rounded-[12px] border-line text-sm" placeholder="Tìm thực phẩm khác để gán" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className={BUTTON}>Tìm</button>
      </form>
      {results.map((f) => (
        <button key={f.id} type="button" className="flex justify-between rounded-[12px] bg-base-200 px-3 py-2 text-left text-sm" onClick={() => onPick(f.id)}>
          <span>{f.name_vi} <span className="text-muted">· {f.code}</span></span><span className="tabular text-muted">{Math.round(f.kcal)} kcal</span>
        </button>
      ))}
    </div>
  );
}

// A food's details before linking a name to it: its values per 100 g next to the AI's latest estimate for the name
// (also per 100 g), its group, source and everyday names. Nothing changes until "Gán" is pressed.
function FoodPreview({ foodId, flag, onClose, onLink }) {
  const { api } = useApp();
  const [food, setFood] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setFood(null);
    setError(null);
    if (foodId) api.get(`/admin/foods/${foodId}`).then(setFood).catch((e) => setError(e.message));
  }, [api, foodId]);

  const ex = flag?.example || {};
  const ai = ex.grams > 0 && ex.ai ? ex.ai : null;
  const aiPer100 = (k) => (ai ? ((Number(ai[k]) || 0) * 100) / ex.grams : null);
  const rows = [['kcal', 'calories', 'kcal', 0], ['protein_g', 'protein_g', 'Đạm (g)'], ['fat_g', 'fat_g', 'Béo (g)'], ['carbs_g', 'carbs_g', 'Tinh bột (g)'], ['fiber_g', 'fiber_g', 'Xơ (g)']];

  return (
    <Sheet open={!!foodId} onClose={onClose} title={food?.name_vi || 'Thực phẩm'}
      badge={food && <span className="text-xs text-muted">{[food.name_en, `mã ${food.code}`, food.group].filter(Boolean).join(' · ')}</span>}>
      {!food && !error && <div className="skeleton h-48 rounded-[16px]" />}
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      {food && (
        <div className="flex flex-col gap-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="py-1 font-semibold">Trên 100 g</th>
                <th className="py-1 text-right font-semibold">{food.name_vi}</th>
                {ai && <th className="py-1 text-right font-semibold">AI ước tính "{flag.name}"</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map(([key, aiKey, label, d]) => (
                <tr key={key}>
                  <td className="py-2 text-muted">{label}</td>
                  <td className="tabular py-2 text-right font-bold">{num(food[key], d ?? 1)}</td>
                  {ai && <td className="tabular py-2 text-right">{num(aiPer100(aiKey), d ?? 1)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          {ai && <p className="text-xs text-muted">Số AI: lần gần nhất {num(ex.grams, 0)} g{ex.dish ? ` trong "${ex.dish}"` : ''}, quy ra 100 g để so.</p>}

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-muted">Nguồn</dt><dd>{SOURCES[food.origin] || food.edition}</dd>
            <dt className="text-muted">Hệ số chín</dt><dd>{num(food.cooked_factor ?? food.default_factor, 2)}{food.cooked_factor == null && ' (theo nhóm)'}</dd>
            <dt className="text-muted">Tên gọi khác</dt><dd>{food.aliases.length ? food.aliases.join(', ') : 'Chưa có'}</dd>
          </dl>

          <p className="rounded-[14px] bg-base-200 p-3 text-sm">
            Gán: "<b>{flag.name}</b>" thành tên gọi khác của <b>{food.name_vi}</b>. Các lần phân tích sau dùng số của thực phẩm này; bữa đã ghi không đổi.
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn btn-primary h-11 flex-1 rounded-[14px] border-0 font-bold" onClick={() => onLink(food.id)}>Gán</button>
            <button type="button" className="btn h-11 rounded-[14px] border-line bg-base-100 px-5" onClick={onClose}>Huỷ</button>
          </div>
        </div>
      )}
    </Sheet>
  );
}

// Neither table has it: keep the AI's latest estimate for the name, per 100 g, as a food of its own.
function EstimateForm({ flag, groups, onSave }) {
  const ex = flag.example;
  const per100 = (v) => Math.round(((Number(v) || 0) * 1000) / ex.grams) / 10;
  const [nameVi, setNameVi] = useState(flag.name.charAt(0).toUpperCase() + flag.name.slice(1));
  const [group, setGroup] = useState('');
  return (
    <form className="flex flex-col gap-2 rounded-[14px] bg-base-200 p-3 text-sm"
      onSubmit={(e) => { e.preventDefault(); onSave({ ai_estimate: true, name_vi: nameVi, group_code: group }); }}>
      <p>
        Lần AI ước tính gần nhất: {Math.round(ex.grams)} g → <b>{Math.round(per100(ex.ai.calories))} kcal</b> · Đ {per100(ex.ai.protein_g)} ·
        B {per100(ex.ai.fat_g)} · TB {per100(ex.ai.carbs_g)} trên 100 g
      </p>
      <p className="text-xs text-muted">Lưu thành thực phẩm "AI ước tính": các lần phân tích sau dùng đúng số này, không đoán lại. Tính theo khối lượng khi ăn (hệ số chín 1).</p>
      <div className="flex flex-wrap gap-2">
        <input required className="input h-10 min-w-0 flex-1 rounded-[12px] border-line text-sm" placeholder="Tên tiếng Việt" value={nameVi} onChange={(e) => setNameVi(e.target.value)} />
        <select required className="select h-10 rounded-[12px] border-line text-sm" value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="">Chọn nhóm…</option>
          {groups.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
        </select>
        <button type="submit" className="btn btn-primary h-10 min-h-10 rounded-[12px] border-0 px-4 text-sm">Lưu vào bảng</button>
      </div>
    </form>
  );
}

function FlagRow({ flag, groups, onResolve, onUsda }) {
  const [picking, setPicking] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const ex = flag.example || {};
  const hasEstimate = ex.grams > 0 && ex.ai?.calories != null;
  return (
    <li className="flex flex-col gap-2 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-bold">{flag.name}</p>
          <p className="text-xs text-muted">
            {REASONS[flag.reason]} · {flag.count} lần · gần nhất {when(flag.last_seen_at)}
            {ex.feature && ` · ${FEATURES[ex.feature] || ex.feature}${ex.dish ? `: ${ex.dish}` : ''}`}
            {ex.grams ? ` · ${Math.round(ex.grams)} g, AI ước tính ${Math.round(ex.ai?.calories || 0)} kcal / ${ex.ai?.protein_g ?? 0} g đạm` : ''}
          </p>
          {flag.food && <p className="text-sm">Đã gán: <b>{flag.food.name_vi}</b> ({flag.food.code}){flag.resolved_by && ` · ${flag.resolved_by}`}</p>}
        </div>
      </div>
      {flag.status === 'open' ? (
        <>
          <div className="flex flex-wrap gap-2">
            {flag.candidates.map((c) => (
              <button key={c.code} type="button" className={BUTTON} onClick={() => setPreviewId(c.id)}>Gán: {c.name_vi}</button>
            ))}
            <button type="button" className={BUTTON} onClick={() => setPicking((p) => !p)}>Gán thực phẩm khác…</button>
            <button type="button" className={BUTTON} onClick={() => onUsda(flag)}>Tìm trong USDA…</button>
            {hasEstimate && <button type="button" className={BUTTON} onClick={() => setEstimating((v) => !v)}>Dùng số AI ước tính…</button>}
            <button type="button" className={BUTTON} onClick={() => onResolve(flag.id, { not_in_table: true })}>Bỏ qua</button>
          </div>
          {picking && <FoodPicker onPick={setPreviewId} />}
          <FoodPreview foodId={previewId} flag={flag} onClose={() => setPreviewId(null)}
            onLink={(id) => { setPreviewId(null); onResolve(flag.id, { food_id: id }); }} />
          {estimating && <EstimateForm flag={flag} groups={groups} onSave={(body) => onResolve(flag.id, body)} />}
        </>
      ) : (
        <button type="button" className={`${BUTTON} w-fit`} onClick={() => onResolve(flag.id, { reopen: true })}>Mở lại</button>
      )}
    </li>
  );
}

// Shown as the "Cần xem lại" tab of Thực phẩm; `onChanged` lets it refresh the open count after a resolve.
export default function FoodReviewPage({ onChanged }) {
  const { api } = useApp();
  const [status, setStatus] = useState('open');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [usdaFor, setUsdaFor] = useState(null);

  const load = useCallback(() => api.get(`/admin/food_review_flags?status=${status}`).then(setData).catch((e) => setError(e.message)), [api, status]);
  useEffect(() => { setData(null); load(); }, [load]);

  const resolve = async (id, body) => {
    setError(null);
    try {
      await api.post(`/admin/food_review_flags/${id}/resolve`, body);
      load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm text-muted">Tên nguyên liệu AI dùng mà chưa khớp chắc chắn với bảng thành phần thực phẩm: các nguyên liệu này đang dùng số AI ước tính. Gán một lần, các lần phân tích sau sẽ dùng số của bảng (bữa đã ghi không đổi).</p>
      </div>
      {/* Filter chips (the page's tabs are above): waiting, linked, not in the table. */}
      <div className="flex gap-1.5" role="radiogroup" aria-label="Trạng thái">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="radio" aria-checked={status === t.id} onClick={() => setStatus(t.id)}
            className={`h-9 shrink-0 whitespace-nowrap rounded-full px-3 text-sm font-semibold ${status === t.id ? 'bg-neutral text-neutral-content' : 'bg-base-100'}`}>
            {t.label}{data?.counts?.[t.id] != null ? ` (${data.counts[t.id]})` : ''}
          </button>
        ))}
      </div>
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="skeleton h-48 rounded-[20px]" />}
      {data?.flags.length === 0 && <p className="rounded-[20px] border border-dashed border-line p-5 text-center text-sm text-muted">Không có tên nào.</p>}
      <UsdaSheet open={!!usdaFor} flag={usdaFor} groups={data?.groups || []} onClose={() => setUsdaFor(null)}
        onAdded={() => { setUsdaFor(null); load(); onChanged?.(); }} />
      {data?.flags.length > 0 && (
        <ul className="divide-y divide-line rounded-[20px] bg-base-100 px-4">
          {data.flags.map((f) => <FlagRow key={f.id} flag={f} groups={data.groups || []} onResolve={resolve} onUsda={setUsdaFor} />)}
        </ul>
      )}
    </div>
  );
}
